import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { backendUrl } from '../utils/backendUrl';
import UserTimezoneSelect from './UserTimezoneSelect';
import PasswordHint from './PasswordHint';
import './Login.css'; // Reusing the same styles

const ChangePassword = () => {
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setError(''); // Clear error when user starts typing
        setSuccess(''); // Clear success message
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        // Client-side validation
        if (formData.newPassword !== formData.confirmPassword) {
            setError('New passwords do not match');
            setLoading(false);
            return;
        }

        if (formData.newPassword.length < 15) {
            setError('Password must be at least 15 characters');
            setLoading(false);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(backendUrl('/auth/change-password'), {
                currentPassword: formData.currentPassword,
                newPassword: formData.newPassword,
                confirmPassword: formData.confirmPassword
            }, {
                withCredentials: true,
                headers: token ? {
                    'Authorization': `Bearer ${token}`
                } : undefined
            });

            if (response.data.message) {
                setSuccess(response.data.message);
                setFormData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
                setTimeout(() => {
                    setSuccess('');
                }, 3000);
            }
        } catch (err) {
            console.error("Password change error:", err);
            if (err.response && err.response.data && err.response.data.error) {
                setError(err.response.data.error);
            } else if (err.response && err.response.status === 401) {
                setError("Authentication failed. Please login again.");
                // Redirect to login after a delay
                setTimeout(() => {
                    localStorage.removeItem('token');
                    localStorage.removeItem('username');
                    localStorage.removeItem('userRole');
                    navigate('/login');
                }, 2000);
            } else if (err.response && err.response.status === 400) {
                setError(err.response.data?.error || "Invalid input. Please check your information.");
            } else if (err.message && err.message.includes("Network Error")) {
                setError("Cannot connect to server. Please check if the backend is running.");
            } else {
                setError('Password change failed. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSubmit(e);
        }
    };

    return (
        <div className="login-container">
            <div className="login-box">
                <h2>Change Password</h2>
                <p className="subtitle">Update your account password</p>
                
                {error && <div className="error-message">{error}</div>}
                {success && <div className="success-message">{success}</div>}

                <div className="form-group">
                    <label htmlFor="commray-change-password-tz">Time zone</label>
                    <UserTimezoneSelect showInnerLabel={false} selectId="commray-change-password-tz" embedded />
                </div>
                
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="currentPassword">Current Password</label>
                        <input
                            type="password"
                            id="currentPassword"
                            name="currentPassword"
                            value={formData.currentPassword}
                            onChange={handleChange}
                            onKeyPress={handleKeyPress}
                            required
                            disabled={loading}
                            placeholder="Enter your current password"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="newPassword">New Password</label>
                        <input
                            type="password"
                            id="newPassword"
                            name="newPassword"
                            value={formData.newPassword}
                            onChange={handleChange}
                            onKeyPress={handleKeyPress}
                            required
                            disabled={loading}
                            placeholder="At least 15 characters"
                        />
                        <PasswordHint value={formData.newPassword} />
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm New Password</label>
                        <input
                            type="password"
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            onKeyPress={handleKeyPress}
                            required
                            disabled={loading}
                            placeholder="Confirm your new password"
                        />
                    </div>

                    <button type="submit" disabled={loading} className="login-button">
                        {loading ? (
                            <>
                                <div className="loading-spinner"></div>
                                Changing Password...
                            </>
                        ) : (
                            'Change Password'
                        )}
                    </button>
                </form>

                <div className="signup-link">
                    <button 
                        onClick={async () => {
                            const token = localStorage.getItem('token');
                            if (token) {
                                try {
                                    // Validate token with backend before navigating
                                    const response = await axios.get(backendUrl("/auth/user-info"), {
                                        withCredentials: true,
                                        headers: token ? { 
                                            Authorization: `Bearer ${token}`,
                                            "Content-Type": "application/json"
                                        } : undefined,
                                        timeout: 5000
                                    });
                                    
                                    if (response.data && response.data.username) {
                                        navigate('/dashboard');
                                    } else {
                                        // Token is invalid, clear it and go to login
                                        localStorage.removeItem('token');
                                        localStorage.removeItem('username');
                                        localStorage.removeItem('userRole');
                                        navigate('/login');
                                    }
                                } catch (error) {
                                    // Token validation failed, clear it and go to login
                                    localStorage.removeItem('token');
                                    localStorage.removeItem('username');
                                    localStorage.removeItem('userRole');
                                    navigate('/login');
                                }
                            } else {
                                navigate('/login');
                            }
                        }} 
                        className="back-button"
                        style={{ marginRight: '10px' }}
                    >
                        Back to Dashboard
                    </button>
                    <button 
                        onClick={() => navigate('/login')} 
                        className="back-button"
                        style={{ 
                            background: 'none', 
                            border: '1px solid #ccc', 
                            color: '#666' 
                        }}
                    >
                        Back to Login
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;
